import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { SearchIcon, MapPinIcon, GlobeIcon, MessageCircleIcon, StarIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc, arrayContains } from 'firebase/firestore';
import { db } from '../../firebase';
interface TeacherData {
  id: string;
  fullName: string;
  location: string;
  instruments: string[];
  experience?: string;
  rating?: number;
  reviewCount?: number;
  profilePictureUrl?: string;
  lessonType?: string[];
  online?: boolean;
  inPerson?: boolean;
}
export const TeacherMatchingPage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    userData
  } = useAuth();
  const [teachers, setTeachers] = useState<TeacherData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lessonType, setLessonType] = useState<string>('both');
  const [zipCode, setZipCode] = useState<string>('');
  // Check if user is logged in and email is verified
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    if (!currentUser.emailVerified) {
      navigate('/email-verification');
      return;
    }
    // Check if instruments have been selected
    if (!userData?.instruments || userData.instruments.length === 0) {
      navigate('/instrument-selection');
      return;
    }
    // Mark onboarding as complete
    if (!userData?.onboardingComplete) {
      updateDoc(doc(db, 'students', currentUser.uid), {
        onboardingComplete: true,
        updatedAt: new Date().toISOString()
      });
      updateDoc(doc(db, 'users', currentUser.uid), {
        onboardingComplete: true,
        updatedAt: new Date().toISOString()
      });
    }
    // Load matching teachers
    loadTeachers();
  }, [currentUser, userData, navigate]);
  // Load teachers that match the student's selected instruments
  const loadTeachers = async () => {
    setLoading(true);
    setError('');
    try {
      if (!userData?.instruments || userData.instruments.length === 0) {
        setError('No instruments selected');
        setLoading(false);
        return;
      }
      // Get teachers that teach any of the selected instruments
      const teachersRef = collection(db, 'teachers');
      const teachersSnapshot = await getDocs(teachersRef);
      const matchingTeachers: TeacherData[] = [];
      teachersSnapshot.forEach(doc => {
        const teacherData = doc.data() as TeacherData;
        // Check if teacher teaches any of the student's selected instruments
        const hasMatchingInstrument = teacherData.instruments?.some(instrument => userData.instruments.includes(instrument));
        if (hasMatchingInstrument) {
          matchingTeachers.push({
            id: doc.id,
            ...teacherData,
            rating: teacherData.rating || 5.0,
            reviewCount: teacherData.reviewCount || 0,
            online: teacherData.lessonType?.includes('online') || true,
            inPerson: teacherData.lessonType?.includes('inPerson') || false
          });
        }
      });
      setTeachers(matchingTeachers);
    } catch (error: any) {
      console.error('Error loading teachers:', error);
      setError('Failed to load matching teachers');
    } finally {
      setLoading(false);
    }
  };
  // Filter teachers based on lesson type and location
  const filteredTeachers = teachers.filter(teacher => {
    // Filter by lesson type
    if (lessonType === 'online' && !teacher.online) return false;
    if (lessonType === 'inPerson' && !teacher.inPerson) return false;
    // Filter by zip code (if provided)
    if (lessonType === 'inPerson' && zipCode && teacher.location) {
      // Simple check if location contains the zip code
      // In a real app, you'd use a proper distance calculation
      return teacher.location.includes(zipCode);
    }
    return true;
  });
  // Navigate to message a teacher
  const messageTeacher = (teacherId: string) => {
    navigate(`/teacher-messages?teacherId=${teacherId}`);
  };
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7997">
      <Navbar data-id="element-7998" />
      <main className="pt-20 pb-16" data-id="element-7999">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-8000">
          <div className="text-center mb-10" data-id="element-8001">
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-8002">
              Your Matching Teachers
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto" data-id="element-8003">
              We've found teachers who specialize in{' '}
              {userData?.instruments?.join(', ')}. Contact them to start your
              musical journey!
            </p>
          </div>
          {/* Filters */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8" data-id="element-8004">
            <h2 className="text-xl font-medium mb-4" data-id="element-8005">Filter Teachers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8006">
              <div data-id="element-8007">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-8008">
                  Lesson Type
                </label>
                <select value={lessonType} onChange={e => setLessonType(e.target.value)} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" data-id="element-8009">
                  <option value="both" data-id="element-8010">Both Online & In-Person</option>
                  <option value="online" data-id="element-8011">Online Only</option>
                  <option value="inPerson" data-id="element-8012">In-Person Only</option>
                </select>
              </div>
              {(lessonType === 'inPerson' || lessonType === 'both') && <div data-id="element-8013">
                  <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-8014">
                    Your Zip Code (for in-person lessons)
                  </label>
                  <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="Enter your zip code" className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" data-id="element-8015" />
                </div>}
            </div>
          </div>
          {error && <div className="bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-6 text-center" data-id="element-8016">
              {error}
            </div>}
          {/* Teacher List */}
          {loading ? <div className="text-center py-12" data-id="element-8017">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-deep-pink border-r-2 border-white/30 mb-4" data-id="element-8018"></div>
              <p data-id="element-8019">Finding your perfect teachers...</p>
            </div> : filteredTeachers.length === 0 ? <div className="bg-white/5 rounded-xl p-8 text-center" data-id="element-8020">
              <p className="text-xl mb-4" data-id="element-8021">No matching teachers found</p>
              <p className="text-gray-300 mb-6" data-id="element-8022">
                Try adjusting your filters or check back later as we add more
                teachers.
              </p>
              <button onClick={() => setLessonType('both')} className="bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-6 rounded-lg transition-colors" data-id="element-8023">
                Reset Filters
              </button>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-8024">
              {filteredTeachers.map(teacher => <div key={teacher.id} className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden hover:bg-white/15 transition-colors" data-id="element-8025">
                  <div className="h-40 bg-gradient-to-r from-deep-pink/20 to-navy-900/40 relative" data-id="element-8026">
                    {teacher.profilePictureUrl ? <img src={teacher.profilePictureUrl} alt={teacher.fullName} className="w-full h-full object-cover" data-id="element-8027" /> : <div className="absolute bottom-0 left-0 p-4" data-id="element-8028">
                        <div className="bg-deep-pink/20 rounded-full p-3 w-16 h-16 flex items-center justify-center" data-id="element-8029">
                          <span className="text-2xl font-bold text-deep-pink" data-id="element-8030">
                            {teacher.fullName.charAt(0)}
                          </span>
                        </div>
                      </div>}
                  </div>
                  <div className="p-6" data-id="element-8031">
                    <div className="flex items-start justify-between mb-2" data-id="element-8032">
                      <h3 className="text-xl font-bold" data-id="element-8033">{teacher.fullName}</h3>
                      <div className="flex items-center" data-id="element-8034">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" data-id="element-8035" />
                        <span className="text-sm" data-id="element-8036">
                          {teacher.rating?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-300 text-sm mb-3" data-id="element-8037">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-8038" />
                      <span data-id="element-8039">
                        {teacher.location || 'Location not specified'}
                      </span>
                    </div>
                    <div className="mb-4" data-id="element-8040">
                      <h4 className="text-sm font-medium text-gray-300 mb-1" data-id="element-8041">
                        Teaches
                      </h4>
                      <div className="flex flex-wrap gap-2" data-id="element-8042">
                        {teacher.instruments?.map((instrument, index) => <span key={index} className="bg-white/10 text-xs px-2 py-1 rounded-full" data-id="element-8043">
                            {instrument}
                          </span>)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-8044">
                      {teacher.online && <div className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-8045">
                          <GlobeIcon className="h-3 w-3 mr-1" data-id="element-8046" />
                          Online
                        </div>}
                      {teacher.inPerson && <div className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded-full flex items-center" data-id="element-8047">
                          <MapPinIcon className="h-3 w-3 mr-1" data-id="element-8048" />
                          In-Person
                        </div>}
                    </div>
                    <button onClick={() => messageTeacher(teacher.id)} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center" data-id="element-8049">
                      <MessageCircleIcon className="h-4 w-4 mr-2" data-id="element-8050" />
                      Message Teacher
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
      </main>
      <Footer data-id="element-8051" />
    </div>;
};